'use client';

import Link from 'next/link';
import { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { FadeUpSection, staggerDelay } from '@/components/fade-up-section';
import PageShell from '@/components/page-shell';
import { useI18n } from '@/components/i18n-provider';
import { useBlogArticles, useContent } from '@/hooks/use-content';

export default function BlogPostPage() {
  const pathname = usePathname();
  const postId = Number(pathname?.split('/').filter(Boolean).pop() || 0);
  const c = useContent();
  const { isArabic } = useI18n();
  const blogArticles = useBlogArticles();
  const p = c.blogPage;
  const arrow = isArabic ? '←' : '→';

  const post = useMemo(() => blogArticles.find((a) => a.id === postId) ?? null, [blogArticles, postId]);

  return (
    <PageShell>
      <main className="px-8 md:px-16 py-12 md:py-20">
        <div className="max-w-3xl mx-auto text-start">
          {!post ? (
            <div className="text-center py-16">
              <p className="text-[#5e5e5e] mb-6">{p.notFound}</p>
              <Link href="/blog" className="text-[var(--bm-dark)] font-bold hover:underline">
                {p.backToBlog}
              </Link>
            </div>
          ) : (
            <article>
              <FadeUpSection as="div">
                <span className="text-xs font-bold text-[var(--bm-mid)]">{post.category}</span>
                <p className="text-sm text-[#999] mt-3">
                  {p.author} ·{' '}
                  {new Date(post.created_at).toLocaleDateString(isArabic ? 'ar-EG' : 'en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
                <h1 className="text-3xl md:text-4xl font-black mt-3 mb-6">{post.title}</h1>
                <p className="text-lg text-[#5e5e5e] leading-relaxed mb-8">{post.excerpt}</p>
              </FadeUpSection>

              {post.content.split(/\n\n+/).map((paragraph, idx) => (
                <FadeUpSection key={idx} as="div" delay={staggerDelay(idx, 0.05)} className="text-[#1a1c1c] leading-relaxed mb-6">
                  {paragraph}
                </FadeUpSection>
              ))}

              <FadeUpSection as="div" className="mt-12 pt-8 border-t border-[var(--bm-light)]">
                <Link href="/blog" className="text-sm font-bold text-[var(--bm-dark)] hover:underline">
                  {p.backToBlog} {arrow}
                </Link>
              </FadeUpSection>
            </article>
          )}
        </div>
      </main>
    </PageShell>
  );
}
